import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuEllipsis } from "react-icons/lu";
import { cn } from "./cn";

export interface MenuItem {
  label: string;
  onClick: () => void;
  tone?: "danger";
  hidden?: boolean;
  /** Quando preenchido, o item fica desabilitado e o motivo aparece escrito embaixo. */
  disabledReason?: string | null;
  separatorBefore?: boolean;
}

interface MenuProps {
  items: MenuItem[];
  /** Nome acessível do botão ⋯ — ex.: "Ações para ana@x.com". */
  label: string;
  align?: "start" | "end";
}

const MENU_WIDTH = 224;
const ESTIMATED_HEIGHT = 240;

// O menu é renderizado num portal com posição fixa: dentro de uma tabela com
// overflow-x-auto, um dropdown absoluto seria cortado.
export const Menu: React.FC<MenuProps> = ({ items, label, align = "end" }) => {
  const [position, setPosition] = useState<React.CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const visibleItems = items.filter((item) => !item.hidden);
  const open = position !== null;

  const close = (returnFocus = true) => {
    setPosition(null);
    if (returnFocus) triggerRef.current?.focus();
  };

  const openMenu = () => {
    const rect = triggerRef.current!.getBoundingClientRect();
    const opensUp = rect.bottom + ESTIMATED_HEIGHT > window.innerHeight && rect.top > ESTIMATED_HEIGHT;
    const left =
      align === "end"
        ? Math.max(8, rect.right - MENU_WIDTH)
        : Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8);
    setPosition(
      opensUp ? { left, bottom: window.innerHeight - rect.top + 4 } : { left, top: rect.bottom + 4 }
    );
  };

  const enabledButtons = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("button:not([disabled])") ?? []);

  useEffect(() => {
    if (!open) return;
    enabledButtons()[0]?.focus();

    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) close(false);
    };
    const handleViewportChange = () => close(false);
    document.addEventListener("mousedown", handlePointer);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key === "Tab") {
      close(false);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const buttons = enabledButtons();
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === "ArrowDown" ? index + 1 : index - 1;
    buttons[(next + buttons.length) % buttons.length]?.focus();
  };

  if (visibleItems.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          open ? close(false) : openMenu();
        }}
        className="flex h-11 w-11 items-center justify-center rounded-control text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg focus-visible:outline-none focus-visible:shadow-focus"
      >
        <LuEllipsis size={18} aria-hidden />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            style={{ ...position, width: MENU_WIDTH }}
            className="fixed z-[10060] rounded-card border border-line bg-surface py-1 shadow-popover animate-fade-in"
          >
            {visibleItems.map((item) => (
              <React.Fragment key={item.label}>
                {item.separatorBefore && <div role="separator" className="my-1 border-t border-line" />}
                <button
                  type="button"
                  role="menuitem"
                  disabled={Boolean(item.disabledReason)}
                  onClick={() => {
                    close();
                    item.onClick();
                  }}
                  className={cn(
                    "flex w-full flex-col items-start px-3 py-2 text-left text-body transition-colors",
                    "focus-visible:bg-surface-hover focus-visible:outline-none enabled:hover:bg-surface-hover",
                    "disabled:cursor-not-allowed",
                    item.tone === "danger" ? "text-danger" : "text-fg",
                    item.disabledReason && "opacity-60"
                  )}
                >
                  {item.label}
                  {item.disabledReason && (
                    <span className="mt-0.5 text-caption text-fg-muted">{item.disabledReason}</span>
                  )}
                </button>
              </React.Fragment>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};
